import {
  notificationRule,
  type NotificationKind,
  type Preferences,
  type SocialNotification,
} from '@teaplus/shared';

type NotificationPrefs = Pick<
  Preferences,
  | 'notificationsEnabled'
  | 'notificationRules'
  | 'notificationSounds'
  | 'hideNotificationsDuringPlayback'
  | 'hidePartyRequestsDuringPlayback'
>;

const KIND_OF: Partial<Record<SocialNotification['type'], NotificationKind>> = {
  'chat-message': 'chat',
  recommendation: 'shared',
  watching: 'watching',
  'friend-request': 'friends',
  'friend-accepted': 'friends',
  'join-request': 'party',
  'party-invite': 'party',
};

/** Whether the profile's settings keep this notification out of the corner, and silent. */
export function notificationHidden(
  type: SocialNotification['type'],
  prefs: NotificationPrefs | undefined,
  playerOpen: boolean,
) {
  if (!prefs) return false;
  if (!prefs.notificationsEnabled) return true;
  const kind = KIND_OF[type];
  if (!kind) return false;
  const rule = notificationRule(prefs, kind);
  return !rule.show || (playerOpen && !rule.whilePlaying);
}

/** Whether a notification that does show also makes a sound. */
export function notificationAudible(
  type: SocialNotification['type'],
  prefs: NotificationPrefs | undefined,
) {
  if (!prefs) return true;
  if (!prefs.notificationSounds) return false;
  const kind = KIND_OF[type];
  return !kind || notificationRule(prefs, kind).sound;
}

/** Each tone is its notes, by pitch, when they start and how long they ring, in seconds. */
const NOTIFICATION_TONE = [{ hz: 880, at: 0, seconds: 0.45 }];
/** Two quick rising notes, so a message sounds different from everything else. */
const CHAT_TONE = [
  { hz: 1175, at: 0, seconds: 0.14 },
  { hz: 1568, at: 0.09, seconds: 0.22 },
];
const TONE_VOLUME = 0.14;
/** Several notifications at once (catching up after a reconnect) ring once. */
const QUIET_MS = 1000;

let context: AudioContext | null = null;
let lastRing = 0;

function ring(ctx: AudioContext, tone: typeof NOTIFICATION_TONE) {
  for (const note of tone) {
    const start = ctx.currentTime + note.at;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(TONE_VOLUME, start + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + note.seconds);
    gain.connect(ctx.destination);
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = note.hz;
    osc.connect(gain);
    osc.onended = () => gain.disconnect();
    osc.start(start);
    osc.stop(start + note.seconds);
  }
}

function play(tone: typeof NOTIFICATION_TONE) {
  const asked = Date.now();
  if (asked - lastRing < QUIET_MS || typeof AudioContext === 'undefined') return;
  lastRing = asked;
  try {
    const ctx = (context ??= new AudioContext());
    if (ctx.state === 'running') return ring(ctx, tone);
    // A resume that only succeeds after a later click must not ring long after the notification.
    void ctx
      .resume()
      .then(() => {
        if (Date.now() - asked < 500) ring(ctx, tone);
      })
      .catch(() => undefined);
  } catch {}
}

/** Plays the notification tone. Browsers block sound until the page is first clicked. */
export function playNotificationTone() {
  play(NOTIFICATION_TONE);
}

/** The tone for a new chat message. */
export function playChatTone() {
  play(CHAT_TONE);
}
